import { BaseNodeCommand, type NodeChangeRecord } from '../../../core/commands/base/BaseNodeCommand';
import type { Edge, Node } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';

const NODE_GAP_X = 160;
const COUPLE_GAP_X = 90;
const GENERATION_GAP_Y = 170;
const MAX_LAYOUT_PASSES = 12;

type Position = { x: number; y: number };

interface LayoutUnit {
  id: string;
  members: Node[];
  generation: number;
  width: number;
  currentCenterX: number;
  targetCenterX: number;
  targetLeftX: number;
  memberTargetCenters: Map<string, number>;
}

const getNodeCenter = (node: Node): { x: number; y: number } => ({
  x: (node.x ?? 0) + ((node.w ?? node.width ?? 60) / 2),
  y: (node.y ?? 0) + ((node.h ?? node.height ?? 60) / 2),
});

const getNodeSize = (node: Node | undefined): { width: number; height: number } => ({
  width: node?.w ?? node?.width ?? 60,
  height: node?.h ?? node?.height ?? 60,
});

const getBoundsCenter = (nodes: Node[]): { x: number; y: number } => {
  const centers = nodes.map(getNodeCenter);
  const minX = Math.min(...centers.map((center) => center.x));
  const maxX = Math.max(...centers.map((center) => center.x));
  const minY = Math.min(...centers.map((center) => center.y));
  const maxY = Math.max(...centers.map((center) => center.y));
  return {
    x: minX + (maxX - minX) / 2,
    y: minY + (maxY - minY) / 2,
  };
};

const getAverage = (values: number[]): number =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const addToSetMap = (map: Map<string, Set<string>>, key: string, value: string): void => {
  const values = map.get(key) ?? new Set<string>();
  values.add(value);
  map.set(key, values);
};

const getNodeSortX = (node: Node): number => getNodeCenter(node).x;

const buildSpouseMap = (nodesById: Map<string, Node>, edges: Edge[]): Map<string, Set<string>> => {
  const spouseMap = new Map<string, Set<string>>();
  edges.forEach((edge) => {
    if (edge.type !== 'marriage') {
      return;
    }
    if (!nodesById.has(edge.from) || !nodesById.has(edge.to)) {
      return;
    }
    addToSetMap(spouseMap, edge.from, edge.to);
    addToSetMap(spouseMap, edge.to, edge.from);
  });
  return spouseMap;
};

const buildGenerationMap = (
  nodes: Node[],
  edges: Edge[],
  spouseMap: Map<string, Set<string>>,
): Map<string, number> => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const parentToChildren = new Map<string, Set<string>>();
  const childToParents = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    if (edge.type !== 'child' || !nodeIds.has(edge.from) || !nodeIds.has(edge.to)) {
      return;
    }
    addToSetMap(parentToChildren, edge.from, edge.to);
    addToSetMap(childToParents, edge.to, edge.from);
  });

  const generationById = new Map<string, number>();
  const roots = nodes
    .filter((node) => !(childToParents.get(node.id)?.size))
    .sort((a, b) => getNodeSortX(a) - getNodeSortX(b));

  roots.forEach((node) => generationById.set(node.id, 0));
  nodes.forEach((node) => {
    if (!generationById.has(node.id)) {
      generationById.set(node.id, 0);
    }
  });

  for (let pass = 0; pass < MAX_LAYOUT_PASSES; pass += 1) {
    let changed = false;

    parentToChildren.forEach((children, parentId) => {
      const parentGeneration = generationById.get(parentId) ?? 0;
      children.forEach((childId) => {
        const nextGeneration = parentGeneration + 1;
        if ((generationById.get(childId) ?? 0) < nextGeneration) {
          generationById.set(childId, nextGeneration);
          changed = true;
        }
      });
    });

    spouseMap.forEach((spouses, nodeId) => {
      spouses.forEach((spouseId) => {
        const generation = Math.max(generationById.get(nodeId) ?? 0, generationById.get(spouseId) ?? 0);
        if (generationById.get(nodeId) !== generation) {
          generationById.set(nodeId, generation);
          changed = true;
        }
        if (generationById.get(spouseId) !== generation) {
          generationById.set(spouseId, generation);
          changed = true;
        }
      });
    });

    if (!changed) {
      break;
    }
  }

  return generationById;
};

const buildUnitsForGeneration = (
  generation: number,
  rowNodes: Node[],
  generationById: Map<string, number>,
  spouseMap: Map<string, Set<string>>,
): LayoutUnit[] => {
  const processed = new Set<string>();
  const ordered = rowNodes.slice().sort((a, b) => getNodeSortX(a) - getNodeSortX(b));
  const units: LayoutUnit[] = [];

  ordered.forEach((node) => {
    if (processed.has(node.id)) {
      return;
    }

    const spouseId = Array.from(spouseMap.get(node.id) ?? []).find(
      (candidateId) => !processed.has(candidateId) && generationById.get(candidateId) === generation,
    );
    const spouse = spouseId ? rowNodes.find((candidate) => candidate.id === spouseId) : undefined;
    const members = spouse ? [node, spouse].sort((a, b) => getNodeSortX(a) - getNodeSortX(b)) : [node];
    members.forEach((member) => processed.add(member.id));

    const width = members.reduce((sum, member) => sum + getNodeSize(member).width, 0) +
      (members.length > 1 ? COUPLE_GAP_X : 0);
    const currentCenterX = getAverage(members.map((member) => getNodeCenter(member).x));

    units.push({
      id: members.map((member) => member.id).join('::'),
      members,
      generation,
      width,
      currentCenterX,
      targetCenterX: currentCenterX,
      targetLeftX: currentCenterX - width / 2,
      memberTargetCenters: new Map<string, number>(),
    });
  });

  return units;
};

const getMemberCenterOffsetInUnit = (unit: LayoutUnit, memberId: string): number => {
  let offset = 0;
  for (const member of unit.members) {
    const size = getNodeSize(member);
    if (member.id === memberId) {
      return offset + size.width / 2;
    }
    offset += size.width + COUPLE_GAP_X;
  }
  return unit.width / 2;
};

const computePreservedYPositions = (
  nodes: Node[],
  edges: Edge[],
  spouseMap: Map<string, Set<string>>,
): Map<string, number> => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const yById = new Map(nodes.map((node) => [node.id, node.y ?? 0]));

  for (let pass = 0; pass < MAX_LAYOUT_PASSES; pass += 1) {
    let changed = false;

    edges.forEach((edge) => {
      if (edge.type !== 'child') {
        return;
      }
      const parent = nodesById.get(edge.from);
      const child = nodesById.get(edge.to);
      if (!parent || !child) {
        return;
      }

      const childY = yById.get(child.id) ?? child.y ?? 0;
      const parentHeight = getNodeSize(parent).height;
      const targetParentY = childY - parentHeight - GENERATION_GAP_Y;
      const currentParentY = yById.get(parent.id) ?? parent.y ?? 0;
      if (currentParentY > targetParentY) {
        yById.set(parent.id, targetParentY);
        changed = true;
      }
    });

    spouseMap.forEach((spouses, nodeId) => {
      const nodeY = yById.get(nodeId);
      if (nodeY === undefined) {
        return;
      }

      spouses.forEach((spouseId) => {
        const spouseY = yById.get(spouseId);
        if (spouseY === undefined) {
          return;
        }
        const sharedY = Math.min(nodeY, spouseY);
        if (nodeY !== sharedY) {
          yById.set(nodeId, sharedY);
          changed = true;
        }
        if (spouseY !== sharedY) {
          yById.set(spouseId, sharedY);
          changed = true;
        }
      });
    });

    if (!changed) {
      break;
    }
  }

  return yById;
};

const computeGenogramPositions = (nodes: Node[], edges: Edge[]): Map<string, Position> => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const spouseMap = buildSpouseMap(nodesById, edges);
  const generationById = buildGenerationMap(nodes, edges, spouseMap);
  const yById = computePreservedYPositions(nodes, edges, spouseMap);
  const childToParents = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    if (edge.type === 'child' && nodesById.has(edge.from) && nodesById.has(edge.to)) {
      addToSetMap(childToParents, edge.to, edge.from);
    }
  });

  const getParentIdsForCentering = (childId: string): string[] => {
    const parentIds = new Set(childToParents.get(childId) ?? []);
    Array.from(parentIds).forEach((parentId) => {
      (spouseMap.get(parentId) ?? new Set<string>()).forEach((spouseId) => {
        if (nodesById.has(spouseId)) {
          parentIds.add(spouseId);
        }
      });
    });
    return Array.from(parentIds);
  };

  const generations = Array.from(new Set(generationById.values())).sort((a, b) => a - b);
  const positions = new Map<string, Position>();

  generations.forEach((generation, rowIndex) => {
    const rowNodes = nodes.filter((node) => generationById.get(node.id) === generation);
    const units = buildUnitsForGeneration(generation, rowNodes, generationById, spouseMap);

    units.forEach((unit) => {
      const anchoredMemberTargets = unit.members.flatMap((member) => {
        const parentCenters = getParentIdsForCentering(member.id)
          .map((parentId) => {
            const parent = nodesById.get(parentId);
            const parentPosition = positions.get(parentId);
            if (!parent || !parentPosition) {
              return undefined;
            }
            return parentPosition.x + getNodeSize(parent).width / 2;
          })
          .filter((value): value is number => typeof value === 'number');
        if (!parentCenters.length) {
          return [];
        }
        return [{
          memberId: member.id,
          targetCenterX: getAverage(parentCenters),
        }];
      });

      unit.memberTargetCenters = new Map(
        anchoredMemberTargets.map((target) => [target.memberId, target.targetCenterX]),
      );

      if (!anchoredMemberTargets.length) {
        unit.targetCenterX = unit.currentCenterX;
        unit.targetLeftX = unit.currentCenterX - unit.width / 2;
        return;
      }

      const targetLefts = anchoredMemberTargets.map(
        (target) => target.targetCenterX - getMemberCenterOffsetInUnit(unit, target.memberId),
      );
      const anchoredBoundsLefts = anchoredMemberTargets.map((target) => {
        const member = unit.members.find((candidate) => candidate.id === target.memberId);
        return target.targetCenterX - getNodeSize(member).width / 2;
      });
      unit.targetLeftX = Math.min(...anchoredBoundsLefts, ...targetLefts);
      unit.targetCenterX = unit.targetLeftX + unit.width / 2;
    });

    units.sort((a, b) => (a.targetLeftX - b.targetLeftX) || (a.currentCenterX - b.currentCenterX));

    let cursorRight = -Infinity;
    units.forEach((unit) => {
      const left = Math.max(unit.targetLeftX, cursorRight + NODE_GAP_X);
      const shiftX = left - unit.targetLeftX;
      let cursorX = left;
      unit.members.forEach((member) => {
        const size = getNodeSize(member);
        const targetCenterX = unit.memberTargetCenters.get(member.id);
        const memberX = targetCenterX !== undefined
          ? targetCenterX - size.width / 2 + shiftX
          : cursorX;
        positions.set(member.id, {
          x: memberX,
          y: yById.get(member.id) ?? member.y ?? 0,
        });
        cursorX = memberX + size.width + COUPLE_GAP_X;
      });
      cursorRight = Math.max(...unit.members.map((member) => {
        const position = positions.get(member.id);
        return position ? position.x + getNodeSize(member).width : left + unit.width;
      }));
    });

    const rowCenters = units.map((unit) => getAverage(unit.members.map((member) => {
      const position = positions.get(member.id);
      return position ? position.x + getNodeSize(member).width / 2 : unit.currentCenterX;
    })));
    const desiredCenter = getAverage(units.map((unit) => unit.targetCenterX));
    const actualCenter = getAverage(rowCenters);
    const hasAnchoredChildren = units.some((unit) => unit.memberTargetCenters.size > 0);
    const rowOffsetX = rowIndex === 0 || hasAnchoredChildren ? 0 : desiredCenter - actualCenter;
    if (rowOffsetX !== 0) {
      rowNodes.forEach((node) => {
        const position = positions.get(node.id);
        if (position) {
          positions.set(node.id, { ...position, x: position.x + rowOffsetX });
        }
      });
    }
  });

  return positions;
};

export class ApplyGenogramAutoLayoutCommand extends BaseNodeCommand {
  constructor(
    private readonly pendingNodes: Node[] = [],
    private readonly pendingEdges: Edge[] = [],
  ) {
    super('Genogram Auto Layout', 'Apply full genogram auto layout');
  }

  protected doExecute(): NodeChangeRecord[] {
    const store = useDiagramStore.getState();
    // only include nodes that are people
    const storeNodes = store.nodes.filter((node) => node.type === 'person');
    const nodes = [...storeNodes, ...this.pendingNodes];
    if (nodes.length < 2) {
      return [];
    }

    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = [
      ...store.edges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to)),
      ...this.pendingEdges,
    ];
    const currentCenter = getBoundsCenter(nodes);
    const layoutPositions = computeGenogramPositions(nodes, edges);
    const layoutAsCanvasNodes = nodes.flatMap((node) => {
      const position = layoutPositions.get(node.id);
      return position ? [{ ...node, x: position.x, y: position.y }] : [];
    });
    if (!layoutAsCanvasNodes.length) {
      return [];
    }

    const layoutCenter = getBoundsCenter(layoutAsCanvasNodes);
    const offsetX = currentCenter.x - layoutCenter.x;
    const offsetY = currentCenter.y - layoutCenter.y;
    const layoutCanvasNodeById = new Map(layoutAsCanvasNodes.map((node) => [node.id, node]));
    const pendingIds = new Set(this.pendingNodes.map((node) => node.id));
    const currentById = new Map(nodes.map((node) => [node.id, node]));

    const updates = nodes.flatMap((node) => {
      const current = currentById.get(node.id);
      const layoutCanvasNode = layoutCanvasNodeById.get(node.id);
      if (!current || !layoutCanvasNode) {
        return [];
      }

      const x = Math.round(layoutCanvasNode.x + offsetX);
      const y = Math.round(layoutCanvasNode.y + offsetY);
      if (pendingIds.has(node.id)) {
        current.x = x;
        current.y = y;
        return [];
      }

      if (current.x === x && current.y === y) {
        return [];
      }

      return [{
        nodeId: node.id,
        changes: { x, y },
      }];
    });

    if (!updates.length) {
      return [];
    }

    return (this.getMutationManager() as any).applyBatchNodeChanges(updates);
  }
}
